import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Reveal } from '../components/Reveal';
import { Star } from '../components/icons/WhatsAppIcon';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';
import {
  ArrowRight, Shield, Users, Wrench, BookOpen,
  Coffee, Wine, UtensilsCrossed, Zap, Building2, PartyPopper, Hotel,
} from 'lucide-react';

// Satellite data for animation
const SATELLITES = [
  { id: 'kasse', label: 'Kasse', angle: 0 },
  { id: 'lieferdienste', label: 'Lieferdienste', angle: 36 },
  { id: 'bestellung', label: 'Bestellung', angle: 72 },
  { id: 'personal', label: 'Personal', angle: 108 },
  { id: 'reservierung', label: 'Reservierung', angle: 144 },
  { id: 'loyalty', label: 'Loyalty', angle: 180 },
  { id: 'warenwirtschaft', label: 'Warenwirtschaft', angle: 216 },
  { id: 'buchhaltung', label: 'Buchhaltung', angle: 252 },
  { id: 'reporting', label: 'Reporting', angle: 288 },
  { id: 'social', label: 'Social Media', angle: 324 },
];

// Service areas with images
const SERVICE_AREAS = [
  {
    title: 'Kassensystem & Kartenzahlung',
    insightsSlug: 'kassensystem-kartenzahlung',
    insightsLabel: 'Kassensystemen & Kartenzahlung',
    description: 'Moderne Kassensysteme mit nahtloser Integration von EC- und Kreditkartenzahlung. Schnelle Abwicklung, übersichtliche Oberflächen und automatische Buchung – für einen reibungslosen Tagesabschluss.',
    image: 'https://images.unsplash.com/photo-1647427017067-8f33ccbae493?w=600&h=400&fit=crop'
  },
  {
    title: 'Reservierungssysteme',
    insightsSlug: 'reservierungssysteme',
    insightsLabel: 'Reservierungssystemen',
    description: 'Online-Reservierungen rund um die Uhr, automatische Bestätigungen und intelligente Tischverwaltung. Reduziere No-Shows und optimiere deine Auslastung.',
    image: 'https://images.unsplash.com/photo-1636357725329-c7dc984f3587?w=600&h=400&fit=crop'
  },
  {
    title: 'Personal- und Schichtplanung',
    insightsSlug: 'personal-schichtplanung',
    insightsLabel: 'Personal- und Schichtplanung',
    description: 'Digitale Dienstpläne, Zeiterfassung und Urlaubsverwaltung. Deine Mitarbeiter haben alles auf dem Smartphone, du behältst den Überblick über Kosten und Verfügbarkeiten.',
    image: 'https://images.pexels.com/photos/18999381/pexels-photo-18999381.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop'
  },
  {
    title: 'Smarte Order-Lösungen',
    insightsSlug: 'smarte-order-loesungen',
    insightsLabel: 'smarten Order-Lösungen',
    description: 'Selbstbestellterminals, QR-Code-Bestellung am Tisch oder digitale Speisekarten – für schnelleren Service und höhere Durchschnitts-Bons.',
    image: 'https://images.unsplash.com/photo-1609427955204-d0a737cb2c1a?w=600&h=400&fit=crop'
  },
  {
    title: 'Delivery-Integration',
    insightsSlug: 'delivery-integration',
    insightsLabel: 'Delivery-Integration',
    description: 'Lieferando, Wolt, Uber Eats und mehr – alle Bestellungen in einem System. Automatische Weiterleitung an die Küche, keine doppelte Eingabe.',
    image: 'https://images.unsplash.com/photo-1593929976216-f746e488aa45?w=600&h=400&fit=crop'
  },
  {
    title: 'Loyalty & Kundenbindung',
    insightsSlug: 'loyalty-kundenbindung',
    insightsLabel: 'Loyalty & Kundenbindung',
    description: 'Digitale Stempelkarten, Bonusprogramme und personalisierte Angebote. Binde Stammgäste und gewinne wertvolle Einblicke in deren Verhalten.',
    image: 'https://images.unsplash.com/photo-1565718253569-3156836e2ec0?w=600&h=400&fit=crop'
  },
  {
    title: 'Social Media',
    insightsSlug: 'social-media',
    insightsLabel: 'Social Media',
    description: 'Content-Planung, Posting-Automatisierung und Reichweitenanalyse. Präsentiere dein Restaurant professionell auf Instagram, Facebook und Google.',
    image: 'https://images.pexels.com/photos/3296547/pexels-photo-3296547.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop'
  },
  {
    title: 'Website- & Bewertungsmanagement',
    insightsSlug: 'website-bewertungsmanagement',
    insightsLabel: 'Website & Bewertungsmanagement',
    description: 'Professionelle Online-Präsenz mit Speisekarte, Reservierungsfunktion und aktiver Bewertungspflege auf Google, TripAdvisor und Co.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
  },
  {
    title: 'Reporting & Controlling',
    insightsSlug: 'reporting-controlling',
    insightsLabel: 'Reporting & Controlling',
    description: 'Alle Zahlen im Blick: Umsatz, Warenkosten, Personalkosten, ABC-Analysen – verständlich aufbereitet und jederzeit abrufbar.',
    image: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=600&h=400&fit=crop'
  },
  {
    title: 'Warenwirtschaft & Einkauf',
    insightsSlug: 'warenwirtschaft-einkauf',
    insightsLabel: 'Warenwirtschaft & Einkauf',
    description: 'Bestandsführung, Lieferantenanbindung und automatische Bestellvorschläge – für weniger Waste, mehr Marge und volle Kontrolle über deine Lagerbestände.',
    image: 'https://images.unsplash.com/photo-1567335632614-abebe179111e?w=600&h=400&fit=crop'
  },
];

// Business types
const BUSINESS_TYPES = [
  { icon: Coffee, title: 'Cafés & Bäckereien', description: 'Schnelle Kasse, digitale Treuekarten, automatisierte Bestellung – für hohen Durchlauf bei kleinem Team.' },
  { icon: Wine, title: 'Bars & Nachtgastronomie', description: 'Bargeldlose Zahlung, Getränke-Controlling, Event-Reservierung – auch nach Mitternacht verlässlich.' },
  { icon: UtensilsCrossed, title: 'Restaurants (à la carte)', description: 'Reservierung, Menüplanung, Tischsteuerung, Warenwirtschaft – alles nahtlos integriert.' },
  { icon: Zap, title: 'Schnellrestaurants & Imbisse', description: 'Lieferplattformen, Kiosk-Bestellung, Küchendisplay – für maximale Geschwindigkeit im Service.' },
  { icon: Building2, title: 'Systemgastronomie & Franchise', description: 'Zentrales Reporting, einheitliche Prozesse, mehrstufige Rechte – für Betreiber mit mehreren Standorten.' },
  { icon: PartyPopper, title: 'Event- & Cateringbetriebe', description: 'Mobile Kassen, Projektabrechnung, Personaleinsatzplanung – flexibel und event-ready.' },
  { icon: Hotel, title: 'Hotels mit Gastronomie', description: 'Integration von PMS, Frühstücks-Abrechnung, Minibar-Buchung – ein System für alle Bereiche.' },
];

const BENEFITS = [
  'Unabhängige Systemberatung basierend auf deinem Konzept',
  'Vergleich & Bewertung geeigneter Lösungen – herstellerneutral',
  'Technisches Setup & Integration aller Systeme',
  'Schulung von Teams – praxisnah und verständlich',
  'Laufender Support & Troubleshooting – schnell und verlässlich',
  'Zentrale Betreuung mehrerer Systeme aus einer Hand',
  'Kosten- & Vertragsübersicht – transparent und fair',
];

const DONT_DO = [
  'Keine Hersteller-Exklusivität',
  'Keine One-Size-Fits-All-Lösungen',
  'Keine anonymen Hotlines ohne Verantwortung',
  'Keine KI-Chatbots',
  'Keine Verkaufs-Mentalität',
];

const HERO_BENEFITS = [
  { icon: Shield, title: 'Unabhängige Beratung' },
  { icon: Wrench, title: 'Praxisnahe Umsetzung' },
  { icon: Users, title: 'Persönlicher Kontakt' },
];

const IconTile = ({ icon: Icon, tone = 'purple' }) => (
  <span className={`inline-flex w-12 h-12 items-center justify-center border-2 border-ink flex-shrink-0 ${tone === 'purple' ? 'bg-purple text-off' : 'bg-lilac text-ink'}`}>
    <Icon className="w-6 h-6" />
  </span>
);

const EcosystemDiagram = () => (
  <div className="relative w-full max-w-2xl mx-auto aspect-square" data-testid="ecosystem-diagram">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
      <div className="card-ink card-ink--off card-ink--shadow w-40 h-40 md:w-52 md:h-52 flex flex-col items-center justify-center text-center px-3">
        <div className="flex items-end justify-center gap-1 mb-2" aria-hidden="true">
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 md:w-5 md:h-5 bg-purple" />
            <div className="w-3 h-5 md:w-4 md:h-6 bg-purple mt-0.5" />
          </div>
          <div className="flex flex-col items-center -mx-1 -mb-1">
            <div className="w-5 h-5 md:w-6 md:h-6 bg-deep" />
            <div className="w-4 h-6 md:w-5 md:h-7 bg-deep mt-0.5" />
          </div>
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 md:w-5 md:h-5 bg-lilac border border-ink" />
            <div className="w-3 h-5 md:w-4 md:h-6 bg-lilac border border-ink mt-0.5" />
          </div>
        </div>
        <div className="font-display font-extrabold text-purple text-base md:text-lg leading-tight mt-1">Gastro-Beratung.de</div>
        <div className="text-ink/70 text-sm md:text-base">Persönliche Betreuung</div>
      </div>
    </div>

    <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {SATELLITES.map((satellite, index) => {
        const radius = 44;
        const angleRad = (satellite.angle * Math.PI) / 180;
        const x = 50 + radius * Math.cos(angleRad);
        const y = 50 + radius * Math.sin(angleRad);
        const duration = 2 + (index % 3) * 0.3;
        const delay = index * 0.15;
        return (
          <g key={`line-group-${satellite.id}`}>
            <line x1="50" y1="50" x2={x} y2={y} stroke="rgba(242,238,230,0.6)" strokeWidth="0.4" />
            <circle r="1" fill="#CBB7EE">
              <animate attributeName="cx" values={`${x};50;${x}`} dur={`${duration}s`} repeatCount="indefinite" begin={`${delay}s`} />
              <animate attributeName="cy" values={`${y};50;${y}`} dur={`${duration}s`} repeatCount="indefinite" begin={`${delay}s`} />
              <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;0.1;0.4;0.5;1" dur={`${duration}s`} repeatCount="indefinite" begin={`${delay}s`} />
            </circle>
          </g>
        );
      })}
    </svg>

    {SATELLITES.map((satellite) => {
      const radius = 44;
      const angleRad = (satellite.angle * Math.PI) / 180;
      const x = 50 + radius * Math.cos(angleRad);
      const y = 50 + radius * Math.sin(angleRad);
      return (
        <div key={satellite.id} className="absolute z-30" style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}>
          <div className="badge-ink badge-ink--white shadow-ink-sm text-[0.7rem] md:text-[0.82rem] px-2.5 md:px-3.5 py-1.5 md:py-2 whitespace-nowrap">
            {satellite.label}
          </div>
        </div>
      );
    })}
  </div>
);

const LoesungenPage = () => {
  useSeo(PAGE_SEO.loesungen);
  return (
    <>
      <Header />
      <main data-testid="loesungen-page">

        {/* Hero */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <p className="eyebrow mb-6 md:mb-8"><Star />Lösungen</p>
            <h1 className="display-1 max-w-[22ch] mb-6">
              Digitale Lösungen für moderne Gastronomie – <span className="serif-it text-purple">alles sinnvoll verbunden.</span>
            </h1>
            <p className="text-lg md:text-xl max-w-[56ch] mb-12">
              Wir strukturieren, integrieren und betreuen deine digitalen Systeme – passend zu deinem Betrieb, nicht zu einem Hersteller.
            </p>
            <div className="grid sm:grid-cols-3 gap-5 md:gap-6">
              {HERO_BENEFITS.map((b) => (
                <div key={b.title} className="card-ink flex items-center gap-4 p-5">
                  <IconTile icon={b.icon} />
                  <h3 className="font-display font-bold text-lg leading-tight">{b.title}</h3>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Ecosystem */}
        <section className="sec sec--purple overflow-hidden">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[22ch] mb-4">Ein digitales <span className="serif-it text-lilac">Ökosystem.</span> Eine zentrale Anlaufstelle.</h2>
            <p className="text-lg opacity-90 max-w-[56ch] mb-12 md:mb-16">
              Statt zehn verschiedener Ansprechpartner, Logins und Support-Hotlines – ein Partner, der alles verbindet.
            </p>
            <EcosystemDiagram />
          </Reveal>
        </section>

        {/* Service areas */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <p className="label-caps text-purple mb-4">Bereiche</p>
            <h2 className="display-2 max-w-[20ch] mb-5">Bereiche, die wir abdecken</h2>
            <p className="text-lg md:text-xl max-w-[60ch] mb-12 md:mb-16">
              Von der Bestellung über die Kasse bis zur Buchhaltung – wir begleiten dich entlang deiner gesamten digitalen Wertschöpfungskette.
            </p>

            <div className="grid md:grid-cols-2 gap-8 md:gap-10">
              {SERVICE_AREAS.map((area, index) => (
                <article
                  key={area.insightsSlug}
                  id={area.insightsSlug}
                  className="card-ink card-ink--hover flex flex-col scroll-mt-28"
                  data-testid={`service-area-${index}`}
                >
                  <div className="relative border-b-2 border-ink overflow-hidden">
                    <img
                      src={area.image}
                      alt={`${area.title} in der Gastronomie`}
                      width="600"
                      height="400"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-52 md:h-56 object-cover"
                    />
                    <span className="absolute top-4 left-4 badge-ink badge-ink--white">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-2xl mb-3">{area.title}</h3>
                    <p className="text-[1rem] leading-relaxed mb-5 flex-1">{area.description}</p>
                    <Link
                      to={`/insights/${area.insightsSlug}`}
                      className="inline-flex items-start gap-2 text-purple font-bold hover:underline underline-offset-4"
                      data-testid={`loesungen-insights-link-${area.insightsSlug}`}
                    >
                      <BookOpen className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span>Mehr zu {area.insightsLabel} findest du in unserem Insights-Bereich <ArrowRight className="w-5 h-5 inline -mt-0.5" /></span>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Business types */}
        <section className="sec sec--lilac">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[20ch] mb-4">Für welche Gastronomien wir arbeiten</h2>
            <p className="text-lg max-w-[60ch] mb-12 md:mb-16">
              Jede Gastronomie ist anders – und braucht andere digitale Lösungen. Wir kennen deine Herausforderungen.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {BUSINESS_TYPES.map((type) => (
                <div key={type.title} className="card-ink card-ink--hover p-6">
                  <IconTile icon={type.icon} />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2">{type.title}</h3>
                  <p className="text-[0.98rem]">{type.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* What we do */}
        <section className="sec bg-off">
          <Reveal className="wrap grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <h2 className="display-2 max-w-[18ch] mb-4">Was wir für dich tun</h2>
              <p className="text-lg mb-8 max-w-[52ch]">
                Von der ersten Beratung bis zum laufenden Betrieb – wir sind dein digitaler Partner.
              </p>
              <ul className="rule-list">
                {BENEFITS.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-4 font-display font-semibold text-lg md:text-xl leading-snug">
                    <Star className="flex-none" />{benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 card-ink bg-deep text-off p-7 md:p-10 lg:mt-12">
              <p className="label-caps text-lilac mb-4">Bewusst nicht</p>
              <h3 className="font-display font-bold text-2xl mb-3">Was wir bewusst nicht machen</h3>
              <p className="opacity-90 mb-6">Wir verkaufen keine Tools – wir lösen Probleme.</p>
              <ul className="grid gap-3.5">
                {DONT_DO.map((item) => (
                  <li key={item} className="flex items-center gap-3 font-display font-bold text-lg">
                    <span className="text-lilac" aria-hidden="true">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>

        {/* CTA */}
        <section className="sec sec--purple">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[22ch] mb-5">
              Lass uns gemeinsam deine <span className="serif-it text-lilac">digitale</span> Landschaft strukturieren.
            </h2>
            <p className="text-lg opacity-90 max-w-[50ch] mb-10">
              Kostenfrei, unverbindlich und auf Augenhöhe – so starten wir mit allen neuen Partnern.
            </p>
            <Link to="/erstberatung" className="btn btn--lilac" data-testid="loesungen-cta">
              Kostenlose Beratung starten
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default LoesungenPage;
